import { createFileRoute, Link, useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import * as v from 'valibot';
import { ArrowUpRight } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { FAQ_BLOCKS, getFaqEntries, type FaqBlock } from '@/content/faq';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { FaqAccordion } from './-components/faq-accordion';
import { DiamondGrid, diamondGlows, Squiggle } from './-components/primitives';
import { slugs } from './faq.meta';

export const Route = createFileRoute('/new/faq')({
	// Which section is open. In the URL so a section is linkable — support answering "that's in the
	// donors section" wants a link, not an instruction to scroll and click. `v.fallback` so a stale or
	// hand-typed value opens the first tab instead of 404ing a page that is entirely static.
	validateSearch: v.object({
		section: v.fallback(v.optional(v.picklist(FAQ_BLOCKS)), undefined),
	}),
	component: Component,
});

/**
 * The tab labels. In the CATALOGS, unlike the questions themselves — there are five of them, they are
 * page furniture rather than Comms' document, and the page needs them before the translated content
 * files exist.
 */
function useBlockTitles(): Record<FaqBlock, string> {
	const { t } = useLingui();
	return {
		companies: t`For companies`,
		ngos: t`For social entities`,
		donors: t`For donors`,
		transparency: t`Transparency and security`,
		about: t`About Worldcoo`,
	};
}

/**
 * "Preguntas frecuentes" — the page the footer has been promising since the redesign went live
 * (TEC-3821 B4/B6).
 *
 * The content is NOT in the Lingui catalogs; see `content/faq.ts` for why. It arrives already grouped,
 * and the page keeps the document's own order rather than sorting: Comms wrote it as a funnel, from the
 * companies that fund to the entities that receive.
 */
function Component() {
	const { t } = useLingui();
	const { locale } = useRouteContext({ from: '__root__' });
	const { section } = useSearch({ from: '/new/faq' });
	const navigate = useNavigate({ from: '/new/faq' });
	const titles = useBlockTitles();
	const entries = getFaqEntries(locale);

	// Sections with nothing in them are dropped rather than shown as an empty tab — the same rule the
	// NGO directory applies to its category rows.
	const sections = FAQ_BLOCKS.map((block) => ({
		block,
		items: entries.filter((entry) => entry.block === block),
	})).filter(({ items }) => items.length > 0);

	// Built from the same array the accordions render, so the markup can never answer a question the
	// page does not show — the drift that gets a rich result pulled. One FAQPage for the whole page,
	// not one per tab: the tabs are a reading aid, not five separate documents.
	const faqLd = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		'mainEntity': entries.map((entry) => ({
			'@type': 'Question',
			'name': entry.question,
			// Bullets and the closing line are flattened into the one string schema.org allows, or the
			// rich result would quote only the sentence that introduces the list.
			'acceptedAnswer': {
				'@type': 'Answer',
				'text': [entry.answer, entry.bullets?.join(' '), entry.outro].filter(Boolean).join(' '),
			},
		})),
	};

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Frequently asked questions - Worldcoo`}
				description={t`Answers to the questions companies, social entities and donors ask us most often about microdonations, the Charity Rounding Up and how the funds raised are managed.`}
				jsonLd={[faqLd]}
			/>

			<section className="bg-linen relative overflow-hidden">
				<DiamondGrid
					color="var(--color-brand)"
					opacity={0.35}
					masks={[diamondGlows({ x: '6%', y: '0%' }, { x: '94%', y: '78%' })]}
				/>

				<div className="relative mx-auto flex max-w-[1440px] flex-col items-center px-6 py-16 md:py-24">
					<div className="flex max-w-[842px] flex-col items-center gap-6 text-center">
						{/* One <Trans>, not two `t` fragments around the Squiggle: which word carries the squiggle
						    moves with the language — Spanish squiggles "frecuentes", English "questions" — and two
						    separate msgids cannot express that. */}
						<h1 className="text-4xl font-semibold tracking-[-0.02em] md:text-5xl">
							<Trans>
								Frequently asked <Squiggle>questions</Squiggle>
							</Trans>
						</h1>
						<p className="text-ink/70 text-lg">
							{t`Everything companies, social entities and donors usually want to know before they start.`}
						</p>
					</div>

					{/* CONTROLLED, like the Solutions page's channel tabs: the tab lives in the URL, so a link to
					    a section works even for a visitor already on this page, whom a `defaultValue` would
					    ignore. `replace` so clicking through the five sections by hand does not bury the page
					    they arrived from in the back button. */}
					<Tabs
						value={section ?? sections[0]?.block}
						onValueChange={(value) => {
							void navigate({ search: { section: value as FaqBlock }, replace: true });
						}}
						className="mt-10 w-full items-center gap-8 md:mt-12"
					>
						<TabsList
							variant="line"
							className="hide-scrollbar max-w-full flex-nowrap justify-start gap-2 overflow-x-auto rounded-none border-0 bg-transparent p-0 group-data-horizontal/tabs:h-auto md:justify-center"
						>
							{sections.map(({ block }) => (
								<TabsTrigger
									key={block}
									value={block}
									// Every background carries the `group-data-[variant=line]/tabs-list:` prefix, and it
									// has to: `cn` is tailwind-merge, which only dedupes classes whose modifiers match, so
									// a bare `bg-sand` would not cancel the trigger's own `…:bg-transparent` — both would
									// survive and the library's would win on specificity. Same trap as solution-tabs.tsx.
									//
									// `data-active:hover:text-white` is NOT redundant with `data-active:text-white`. The
									// shared trigger ships `hover:text-foreground`, and tailwind-merge leaves it alone
									// because its modifiers differ from ours — so on the SELECTED pill both apply, both
									// score (0,2,0), and whichever Tailwind emits last wins: dark text on the dark fill.
									// The active+hover pair scores (0,3,0) and settles it.
									className="text-ink group-data-[variant=line]/tabs-list:data-active:bg-ink group-data-[variant=line]/tabs-list:data-active:hover:bg-ink group-data-[variant=line]/tabs-list:bg-sand h-14 flex-none rounded-2xl px-6 text-base font-medium after:hidden group-data-[variant=line]/tabs-list:hover:bg-white data-active:text-white data-active:hover:text-white"
								>
									{titles[block]}
								</TabsTrigger>
							))}
						</TabsList>

						{sections.map(({ block, items }) => (
							// `forceMount` keeps every panel in the served HTML. Radix unmounts an inactive tab by
							// default, which would leave the page showing one section's questions while the FAQPage
							// markup above declares all five — exactly the mismatch the JSON-LD is built from one
							// array to avoid. Radix still marks the inactive ones `hidden`, so only one is visible.
							<TabsContent
								key={block}
								value={block}
								forceMount
								className="flex w-full flex-col items-center data-[state=inactive]:hidden"
							>
								{/* The tab labels the panel for assistive technology, so a second visible heading would
								    only repeat it — but the document outline still wants one. */}
								<h2 className="sr-only">{titles[block]}</h2>
								<FaqAccordion items={items} />
							</TabsContent>
						))}
					</Tabs>
				</div>
			</section>

			{/* The document's own closing block. */}
			<section className="bg-sand">
				<div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-6 py-16 text-center md:py-24">
					<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em]">{t`Still have a question?`}</h2>
					<p className="text-ink/70 text-lg">
						{t`If you haven't found the answer you were looking for, our team will be glad to help.`}
					</p>
					<Link
						to="/new/contact"
						className="bg-ink mt-2 flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-white transition-opacity hover:opacity-90"
					>
						{t`Contact us`}
						<span className="grid size-6 place-items-center rounded-full bg-white/15">
							<ArrowUpRight className="size-4" />
						</span>
					</Link>
				</div>
			</section>

			<Footer />
		</Layout>
	);
}
